import React from "react";
import type { SalesOrderDetailsTimelineItem as TimelineItem } from "components/SalesOrderDetails/types";
import type { PaymentInfoItem, StatusHistoryItem } from "types";

const paidAmountChipStyle: React.CSSProperties = {
  backgroundColor: "#d1fae5",
  color: "#549180",
  padding: "4px 10px",
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: 600,
};

const paidStatusChipStyle: React.CSSProperties = {
  backgroundColor: "#d1fae5",
  color: "#549180",
  padding: "4px 10px",
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: 600,
};

const pendingChipStyle: React.CSSProperties = {
  backgroundColor: "#FFF4E8",
  color: "#ea580c",
  padding: "4px 10px",
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: 600,
};

const partialChipStyle: React.CSSProperties = {
  backgroundColor: "#ffedd5",
  color: "#b45309",
  padding: "4px 10px",
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: 600,
};

function unwrapHistoryDate(raw: unknown): string | Date {
  if (raw == null) return new Date(0);
  if (typeof raw === "object" && raw !== null && "$date" in (raw as object)) {
    return (raw as { $date: string }).$date;
  }
  return raw as string | Date;
}

function toTimestamp(v: unknown): number {
  const t = new Date(unwrapHistoryDate(v) as any).getTime();
  return Number.isFinite(t) ? t : 0;
}

function resolveOrderStatusCode(status: number | undefined): string {
  switch (status) {
    case 100:
      return "Draft";
    case 101:
      return "Confirmed";
    case 300:
      return "Shipping";
    case 301:
      return "Shipped";
    case 302:
    case 401:
      return "Delivered";
    case 400:
      return "Cancelled";
    default:
      return status !== undefined ? `Status ${status}` : "Unknown";
  }
}

function statusLabelFromHistory(log: StatusHistoryItem): string {
  const fromApi =
    String(log.StatusName ?? log.status_name ?? "").trim() ||
    String(log.Note ?? log.note ?? "").trim();
  if (fromApi) return fromApi;
  const code = log.New ?? log.new;
  return resolveOrderStatusCode(typeof code === "number" ? code : undefined);
}

function formatPaymentMethod(method: string | undefined): string {
  const m = String(method ?? "").trim();
  if (!m) return "";
  return m.replace(/_/g, " ");
}

function getPaymentStatus(p: PaymentInfoItem & Record<string, unknown>): string {
  const fromApi = String(p.current_payment_status ?? "").trim();
  if (fromApi) return fromApi;
  if (p.amount_due === 0) return "Paid";
  if (Number(p.amount_paid) > 0) return "Partial Payment";
  return "Pending";
}

function buildStatusLogItems(order: any): TimelineItem[] {
  const statusHistory: StatusHistoryItem[] = Array.isArray(order?.status_history)
    ? order.status_history
    : [];

  return statusHistory.map((log) => {
    const label = statusLabelFromHistory(log);
    const byValue = log.By ?? log.by;
    return {
      type: "status",
      at: unwrapHistoryDate(log.At ?? log.at),
      subtitle: `Status: ${label}`,
      ...(byValue ? { by: byValue } : {}),
    };
  });
}

function buildPaymentLogItems(order: any): TimelineItem[] {
  const paymentInfo: PaymentInfoItem[] = Array.isArray(order?.payment_info)
    ? order.payment_info
    : [];

  return paymentInfo.map((p) => {
    const ext = p as PaymentInfoItem & Record<string, unknown>;
    const status = getPaymentStatus(ext);
    const statusChipStyle =
      status === "Paid"
        ? paidStatusChipStyle
        : status === "Pending"
          ? pendingChipStyle
          : partialChipStyle;

    return {
      type: "payment",
      at: p.payment_date,
      subtitle: (
        <div className="d-flex align-items-center flex-wrap gap-2">
          <span style={paidAmountChipStyle} className="order-card-log">
            ${Number(p.amount_paid || 0).toFixed(2)}
            {p.method ? ` via ${formatPaymentMethod(p.method)}` : ""}
          </span>
          <span style={statusChipStyle}>{status}</span>
        </div>
      ),
      ...(p.paid_by ? { by: p.paid_by } : {}),
    };
  });
}

function buildSendLogItems(order: any): TimelineItem[] {
  const quoteLogs = Array.isArray(order?.send_quote_log) ? order.send_quote_log : [];
  const invoiceLogs = Array.isArray(order?.send_invoice_log) ? order.send_invoice_log : [];

  const quoteItems: TimelineItem[] = quoteLogs.map((l: any) => {
    const label = String(l?.StatusName ?? l?.status_name ?? "Quote Sent").trim() || "Quote Sent";
    return {
      type: "quote_sent",
      subtitle: `Status: ${label}`,
      at: l?.sent_at,
      by: {
        id: String(l?.user_id || ""),
        name: String(l?.user_name || ""),
      },
    };
  });

  const invoiceItems: TimelineItem[] = invoiceLogs.map((l: any) => {
    const label = String(l?.StatusName ?? l?.status_name ?? "Invoice Sent").trim() || "Invoice Sent";
    return {
      type: "invoice_sent",
      subtitle: `Status: ${label}`,
      at: l?.sent_at,
      by: {
        id: String(l?.user_id || ""),
        name: String(l?.user_name || ""),
      },
    };
  });

  return [...quoteItems, ...invoiceItems];
}

/** Build order log timeline from order GET (`status_history`, `payment_info`, send logs). */
export function buildSalesOrderOrderLogItems(order: any): TimelineItem[] {
  if (!order) return [];

  const items = [
    ...buildStatusLogItems(order),
    ...buildPaymentLogItems(order),
    ...buildSendLogItems(order),
  ];

  return items.sort((a, b) => toTimestamp(b.at) - toTimestamp(a.at));
}
